import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Play, ArrowLeft as ArrowLeftIcon } from 'lucide-react';

const ScrollSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

// Before/After Slider
const BeforeAfterSlider = ({ beforeImage, afterImage }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setSliderPosition(percent);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video overflow-hidden rounded-2xl cursor-ew-resize"
      onMouseMove={(e) => handleMove(e.clientX)}
      onTouchMove={(e) => handleMove(e.touches[0].clientX)}
    >
      <img src={afterImage} alt="After" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPosition}%` }}>
        <img
          src={beforeImage}
          alt="Before"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${100 / (sliderPosition / 100)}%` }}
        />
      </div>
      <div
        className="absolute top-0 bottom-0 w-1 bg-primary"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg">
          <ArrowLeftIcon className="w-4 h-4 text-white" />
          <ArrowRight className="w-4 h-4 text-white" />
        </div>
      </div>
      <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur rounded-lg text-white text-xs">Before</div>
      <div className="absolute top-4 right-4 px-3 py-1 bg-primary rounded-lg text-white text-xs font-medium">After</div>
    </div>
  );
};

const PortfolioPage = () => {
  const projects = [
    {
      id: 1,
      title: 'Excellence Coaching Institute',
      category: 'Digital Marketing & Video',
      description: 'Complete digital transformation including social media management, promotional videos, and performance marketing campaigns.',
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80',
      results: ['+300% leads', '45K reach/month', '2.5x ROI'],
      services: ['Social Media', 'Video Production', 'Paid Ads'],
    },
    {
      id: 2,
      title: 'Urban Fitness Studio',
      category: 'Brand Film & Identity',
      description: 'Cinematic brand film and visual identity refresh that positioned them as a premium fitness destination.',
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80',
      results: ['500K+ views', '3x inquiries', 'Premium positioning'],
      services: ['Video Production', 'Branding', 'Social Media'],
      beforeAfter: {
        before: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80',
        after: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80',
      },
    },
    {
      id: 3,
      title: 'TechLearn Academy',
      category: 'Website & Marketing',
      description: 'Modern website redesign and integrated marketing strategy that scaled their online presence nationally.',
      image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80',
      results: ['400% traffic', '200+ sign-ups/mo', 'Page 1 rankings'],
      services: ['Web Development', 'SEO', 'Content Marketing'],
    },
    {
      id: 4,
      title: 'Artisan Coffee Co.',
      category: 'Branding & Social',
      description: 'Complete brand identity and social media content strategy for a new artisan coffee brand launch.',
      image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&q=80',
      results: ['10K followers', '2M+ views', '150% sales'],
      services: ['Branding', 'Photography', 'Social Media'],
    },
    {
      id: 5,
      title: 'Wellness Hub Clinic',
      category: 'Digital Presence',
      description: 'Healthcare marketing including website, patient testimonials, and local SEO optimization.',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80',
      results: ['5x inquiries', '4.9★ rating', 'Top 3 local'],
      services: ['Web Design', 'Video', 'SEO'],
    },
    {
      id: 6,
      title: 'Leadership Podcast',
      category: 'Podcast Production',
      description: 'End-to-end podcast production including branding, editing, and distribution strategy.',
      image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=800&q=80',
      results: ['50K downloads', 'Top 50 chart', '25 sponsors'],
      services: ['Podcast', 'Branding', 'Marketing'],
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Our Work</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Results That Speak{' '}
              <span className="text-gradient">For Themselves</span>
            </h1>
            <p className="text-fp-textMuted text-lg leading-relaxed">
              Explore our portfolio of successful projects. Every piece of work is 
              designed to drive measurable outcomes for our clients.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-24">
            {projects.map((project, index) => (
              <ScrollSection key={project.id} delay={0.1}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? '' : ''
                }`}>
                  {/* Image */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    {project.beforeAfter ? (
                      <BeforeAfterSlider
                        beforeImage={project.beforeAfter.before}
                        afterImage={project.beforeAfter.after}
                      />
                    ) : (
                      <div className="relative overflow-hidden rounded-2xl group">
                        <motion.img
                          src={project.image}
                          alt={project.title}
                          className="w-full aspect-video object-cover"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.6 }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-fp-bg/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-6 left-6 flex items-center gap-2 text-white">
                            <Play className="w-5 h-5" />
                            <span className="font-medium">View Project</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <p className="text-primary text-sm font-medium mb-2">{project.category}</p>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{project.title}</h2>
                    <p className="text-fp-textMuted mb-6 leading-relaxed">{project.description}</p>

                    {/* Results */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {project.results.map((result, i) => (
                        <div key={i} className="glass-card p-4 rounded-xl text-center">
                          <span className="text-primary font-bold text-lg block">
                            {result.split(' ')[0]}
                          </span>
                          <span className="text-fp-textMuted text-xs">
                            {result.split(' ').slice(1).join(' ')}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Services */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.services.map((service, i) => (
                        <span
                          key={i}
                          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-fp-textMuted"
                        >
                          {service}
                        </span>
                      ))}
                    </div>

                    <button className="btn-ghost inline-flex items-center gap-2" data-testid={`portfolio-view-${project.id}`}>
                      View Case Study
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </ScrollSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <ScrollSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Create Your Success Story?
            </h2>
            <p className="text-fp-textMuted text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help you achieve results like these for your brand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book-call"
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="portfolio-cta-call"
              >
                Book a Strategy Call
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/get-proposal"
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="portfolio-cta-proposal"
              >
                Get a Proposal
              </Link>
            </div>
          </ScrollSection>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;
